/**
 * Checks of a whole path against the robot rather than of its centre line: the clearance of the
 * body swept along the path (global_path_harness checks every 1 cm / 1° of a plan the same way)
 * and the speed at which the path's heading changes no faster than the robot can turn.
 *
 * The TCR base is a swerve drive and can crab, so the body's heading along a path is the path's
 * sampled yaw (tangent or fixed), not the direction of travel: a fixed-heading path sweeps the
 * body sideways, a tangent one turns it with the curve.
 */
import { NO_OBSTACLE_M, OFF_MAP_CLEARANCE, MIN_CLEARANCE_M, TCR_FOOTPRINT, footprintClearance } from "./footprint.js";
import { normalizeAngle, worldToPixel } from "./geometry.js";
/**
 * TCR limits from the robot's route_network.json `robot.limits` (tcr robot_config). The minimum
 * clearance in its `safety` block is MIN_CLEARANCE_M.
 */
export const ROBOT_LIMITS = {
    maxLinearVelocity: 0.3,
    maxAngularVelocity: 0.4,
    maxSteeringVelocity: 2.0,
    maxWheelSpeed: 0.8,
};
/**
 * Speed a path without a speedLimit is exported (edge `time`) and checked at, m/s: the robot's
 * max_linear_velocity. Shared so the bundle, validation and the editor agree.
 */
export const DEFAULT_ROUTE_SPEED = ROBOT_LIMITS.maxLinearVelocity;
/** Densify so consecutive checked poses are at most this far apart (the harness' 1 cm / 1°). */
export const SWEEP_STEP_M = 0.01;
export const SWEEP_STEP_RAD = Math.PI / 180;
/**
 * Footprint clearance at every pose along `samples`, densified to SWEEP_STEP_M / SWEEP_STEP_RAD
 * (x, y linear, yaw along the shorter way round). 5 cm samples alone miss what the body's
 * corners sweep while the heading turns: a 30° turn between two samples swings the TCR's corners
 * (0.73 m out) 2.5 cm past the chord between them; at 1° it is 0.03 mm.
 *
 * Fast path: most poses are nowhere near anything. Bilinear field values are within √2 cells of
 * the true distance (they interpolate exact distances at cell centres no further than that), so
 * a pose's clearance is at least
 *
 *   field(centre) − R − 2√2·res − res/2      (R = the footprint's circumscribed radius)
 *
 * and a pose whose bound is ≥ MIN_CLEARANCE_M (with R wholly on the map) is clear without the
 * ~1300-point evaluation. The bound is loose for a long, narrow body (R = 0.73 m against a 0.235 m
 * half width), so it pays off in open space, not along corridors. `min` stays exact: skipped
 * poses whose bound is below the best evaluated clearance are evaluated, lowest bound first.
 */
export function sweptClearance(field, samples, fp = TCR_FOOTPRINT) {
    const xs = [];
    const ys = [];
    const yaws = [];
    const ss = [];
    const push = (x, y, yaw, s) => {
        xs.push(x);
        ys.push(y);
        yaws.push(yaw);
        ss.push(s);
    };
    if (samples.length)
        push(samples[0].x, samples[0].y, samples[0].yaw, samples[0].s);
    for (let i = 1; i < samples.length; i++) {
        const a = samples[i - 1];
        const b = samples[i];
        const dyaw = normalizeAngle(b.yaw - a.yaw);
        // −1e-9: a 5 cm step is 5 sub-steps, not 6 from rounding.
        const n = Math.max(1, Math.ceil(Math.hypot(b.x - a.x, b.y - a.y) / SWEEP_STEP_M - 1e-9), Math.ceil(Math.abs(dyaw) / SWEEP_STEP_RAD - 1e-9));
        for (let k = 1; k <= n; k++) {
            const t = k / n;
            push(a.x + (b.x - a.x) * t, a.y + (b.y - a.y) * t, a.yaw + dyaw * t, a.s + (b.s - a.s) * t);
        }
    }
    const N = xs.length;
    const { meta, dist } = field;
    const W = meta.width;
    const H = meta.height;
    const res = meta.resolution;
    const radius = Math.max(0, ...fp.map((p) => Math.hypot(p.x, p.y)));
    const margin = radius + 2 * Math.SQRT2 * res + res / 2 + 1e-6;
    const rPx = radius / res;
    const clr = new Float64Array(N).fill(NaN); // NaN = not evaluated, known clear
    const bound = new Float64Array(N);
    let best = Infinity;
    let bestAt = 0;
    const evaluate = (i) => {
        const c = footprintClearance(field, { x: xs[i], y: ys[i], theta: yaws[i] }, fp);
        clr[i] = c;
        if (c < best || (c === best && ss[i] < bestAt)) {
            best = c;
            bestAt = ss[i];
        }
    };
    for (let i = 0; i < N; i++) {
        const { u, v } = worldToPixel(xs[i], ys[i], meta);
        const fx = u - 0.5;
        const fy = v - 0.5;
        if (fx - rPx < 0 || fy - rPx < 0 || fx + rPx >= W - 1 || fy + rPx >= H - 1) {
            bound[i] = -Infinity;
            evaluate(i);
            continue;
        }
        const x0 = Math.floor(fx);
        const y0 = Math.floor(fy);
        const ax = fx - x0;
        const ay = fy - y0;
        const top = dist[y0 * W + x0] * (1 - ax) + dist[y0 * W + x0 + 1] * ax;
        const bot = dist[(y0 + 1) * W + x0] * (1 - ax) + dist[(y0 + 1) * W + x0 + 1] * ax;
        bound[i] = top * (1 - ay) + bot * ay - margin;
        if (bound[i] < MIN_CLEARANCE_M)
            evaluate(i);
    }
    // Exact minimum: refine skipped poses that could still beat it. On a map with no occupied
    // cell every bound is ~NO_OBSTACLE_M; nothing there is worth refining.
    const open = [];
    for (let i = 0; i < N; i++)
        if (Number.isNaN(clr[i]) && bound[i] < best && bound[i] < NO_OBSTACLE_M / 2)
            open.push(i);
    open.sort((a, b) => bound[a] - bound[b]);
    for (const i of open) {
        if (bound[i] >= best)
            break;
        evaluate(i);
    }
    if (best === Infinity && N) {
        // Empty map: report the clearance of the first pose rather than nothing.
        evaluate(0);
    }
    const offMap = [];
    const collisions = [];
    const tight = [];
    const lists = { 1: tight, 2: collisions, 3: offMap };
    const levelOf = (c) => (Number.isNaN(c) || c >= MIN_CLEARANCE_M ? 0 : c === OFF_MAP_CLEARANCE ? 3 : c < 0 ? 2 : 1);
    let cur = null;
    let curLevel = 0;
    for (let i = 0; i < N; i++) {
        const c = clr[i];
        const level = levelOf(c);
        if (level !== curLevel) {
            cur = null;
            curLevel = level;
            if (level) {
                cur = { from: ss[i], to: ss[i], min: c, at: ss[i] };
                lists[level].push(cur);
            }
        }
        else if (cur) {
            cur.to = ss[i];
            if (c < cur.min) {
                cur.min = c;
                cur.at = ss[i];
            }
        }
    }
    return { min: best, at: bestAt, offMap, collisions, tight, poses: N };
}
/**
 * Heading change is measured over at least this much path. samplePath interpolates a polyline
 * with 2.5 cm segments, so at a 1 cm sample step the heading turns in jumps at its vertices and
 * per-sample |Δyaw|/Δs reads 2–3× what 5 cm sampling does on the same bend; over 10 cm the
 * estimate is within 10% of it at any step.
 */
export const TURN_RATE_WINDOW_M = 0.1;
/**
 * Speed limit from the body's turn rate: driving at v along a stretch whose heading changes by
 * k rad/m turns the body at k·v rad/s, so v ≤ maxAngularVelocity / k. k is |Δyaw|/Δs as
 * maxCurvature measures it, but over TURN_RATE_WINDOW_M of path. A fixed-heading path never
 * turns. Consecutive samples at the same s with different headings (rotation in place;
 * samplePath never makes them) give 0.
 */
export function turnRateSpeedLimit(samples, limits = ROBOT_LIMITS) {
    // Unwrapped heading, so a window across ±π measures the turn, not the wrap.
    const yaw = [];
    for (let i = 0; i < samples.length; i++) {
        const a = samples[i];
        if (i === 0) {
            yaw.push(a.yaw);
            continue;
        }
        const d = normalizeAngle(a.yaw - samples[i - 1].yaw);
        if (Math.abs(d) > 1e-9 && a.s - samples[i - 1].s <= 1e-9)
            return { speed: 0, yawPerMetre: Infinity, at: a.s };
        yaw.push(yaw[i - 1] + d);
    }
    let k = 0;
    let at = 0;
    for (let i = 0, j = 0; i < samples.length - 1; i++) {
        j = Math.max(j, i + 1);
        while (j < samples.length - 1 && samples[j].s - samples[i].s < TURN_RATE_WINDOW_M)
            j++;
        const ds = samples[j].s - samples[i].s;
        const ki = ds > 1e-9 ? Math.abs(yaw[j] - yaw[i]) / ds : 0;
        if (ki > k + 1e-12) {
            k = ki;
            at = samples[i].s;
        }
    }
    return { speed: k > 1e-9 ? limits.maxAngularVelocity / k : Infinity, yawPerMetre: k, at };
}
//# sourceMappingURL=pathcheck.js.map